import { IObjectsHasTags, ITags } from '@llws/lift-entity-interfaces';
import { ActionContext } from 'vuex';
import { IQueryCommandOptions } from '@llws/dynamic-router-utils';
import {
  IApiResponse, IFilterPayloadV2, IMetaResponse, IModifyPayload, subsetObject,
} from '@/shared';
import { IPostStore } from '@/shared/interfaces/post-store';
import tags from '@/shared/services/tags';
import { INewTranslationSettingsStoreConfig } from '@/shared/interfaces/new-translation-settings';
import { IAction, IStateStrict } from '@/shared/interfaces/store-base';
import { adminRequest } from '@/shared/request-configs/admin-request';
import { StoreBaseImplementationV2 } from '@/shared/classes/store-base-implementation-v2';

// Base store configuration
const adminGatewayResourcePath: string = '/website-api/tags';
const listViewName: string = '';
const showLogging: boolean = false;

const defaultGetListFilters: IQueryCommandOptions = {};

const defaultGetSingleFilters: IQueryCommandOptions = {};

const translationSettings: INewTranslationSettingsStoreConfig = {
  model: 'tags',
  keys: ['name'],
};

// Instantiate the base store with the params
const base = new StoreBaseImplementationV2<ITags>({
  adminGatewayResourcePath,
  defaultGetSingleFilters,
  defaultGetListFilters,
  listViewName,
  showLogging,
  translationSettings,
});

interface ITagState extends IStateStrict<ITags> {
  photoTags: IApiResponse<IObjectsHasTags[], IMetaResponse> | null;
  photoTagsToRemove: IObjectsHasTags[];
}

// Extend StoreBaseImplementation object to add custom state/getters/mutations/actions.
export default {
  namespaced: base.namespaced, // 'tags/'

  state: {
    ...base.state,
    photoTags: null as IApiResponse<ITags[], null> | null,
    photoTagsToRemove: [],
  } as ITagState,

  getters: {
    ...base.getters,

    photoTags: (
      state: ITagState,
    ): ITags[] => Array.from(((state.photoTags?.data || []).filter((t) => t)), (oht) => oht.tag!),

    photoTagObjects: (
      state: ITagState,
    ): IObjectsHasTags[] => (state.photoTags?.data || [])
      .filter((t) => !state.photoTagsToRemove.some((tr) => tr.tag?.name === t.tag?.name)),

    photoTagsToRemove: (state: ITagState): IObjectsHasTags[] => (state.photoTagsToRemove || []).filter((oht) => oht.id),
  },

  mutations: {
    ...base.mutations,

    setPhotoTags: (state: ITagState, payload: IApiResponse<IObjectsHasTags[], IMetaResponse>) => {
      state.photoTags = payload;
    },

    addPhotoTagToRemove: (state: ITagState, payload: IObjectsHasTags) => {
      state.photoTagsToRemove = [ ...state.photoTagsToRemove, payload ];
    },

    setPhotoTagsToRemove: (state: ITagState, payload: IObjectsHasTags[]) => {
      state.photoTagsToRemove = payload;
    },
  },

  actions: {
    ...base.actions,

    saveBuildingPhotoTags: async (
      ctx: IAction<IStateStrict<ITags>>,
      payload: IModifyPayload<number, IObjectsHasTags[]>,
    ) => {
      // Add Tag Objects
      const tagsToAdd: ITags[] = payload.data.map((t) => t.tag!).filter((t) => !t.id);
      if (tagsToAdd.length) {
        await ctx.dispatch('insertMany', { data: tagsToAdd } as IModifyPayload);
      }

      // Object tag intersections to add.
      const objectHasTagsToAdd: IObjectsHasTags[] = payload.data.filter((oht) => !oht.id).map((oht) => ({
        ...subsetObject(oht, ['tag']),
        tagId: ctx.getters.list.find((at: ITags) => at.name === oht.tag!.name)!.id,
      }));
      if (objectHasTagsToAdd.length) {
        await adminRequest.post('/website-api/objects_has_tags', objectHasTagsToAdd);
      }

      // Remove tag associations
      if (ctx.getters.photoTagsToRemove.length) {
        await adminRequest.delete(
          '/website-api/objects_has_tags',
          Array.from(ctx.getters.photoTagsToRemove, (oht: IObjectsHasTags) => oht.id!),
        );
        ctx.commit('setPhotoTagsToRemove', []);
      }

      // Get fresh data.
      await ctx.dispatch('getBuildingPhotoTags', { id: payload.id! });
    },

    getBuildingPhotoTags: async (
      ctx: ActionContext<IPostStore, any>,
      payload: IFilterPayloadV2<number>,
    ) => {
      const result = await tags.getByModelAndObjectId('building-photos', payload.id!);
      ctx.commit('setPhotoTags', result as IApiResponse<IObjectsHasTags[], IMetaResponse>);
    },

    getTags: async (
      ctx: ActionContext<IPostStore, any>,
      payload: any,
    ) => {
      const photoTags = await tags.getTags(payload.clientId);
      return photoTags;
    },
  },
};
